/*
 * CMP-014 Product gallery — Phase A1. No dependencies (the main slot's image-error/retry comes from nc-card.js).
 * Thumbnails use roving tabindex: Tab reaches only the active one; ArrowLeft/ArrowUp and ArrowRight/ArrowDown
 * move and select (wrapping), Home/End jump to the extremes, click selects. The strip is a column from 834 px and a
 * row below, so both axes are handled in both layouts: Up/Left = previous, Down/Right = next (natural in a column
 * for Up/Down and in a row for Left/Right; the other pair is a harmless alias). Selecting swaps the main image,
 * moves aria-current, announces "Imagen n de N" and fires a bubbling `nc-gallery:change` {id, index} only when the
 * index actually changes.
 */
(function () {
  // next thumbnail index for a key, wrapping; null when the key is not handled
  function getNextThumbIndex(key, current, count) {
    var next = { ArrowLeft: current - 1, ArrowUp: current - 1, ArrowRight: current + 1, ArrowDown: current + 1, Home: 0, End: count - 1 }[key];
    if (next === undefined || count < 1) return null;
    return (next + count) % count;
  }

  // swipe on the main image (Javi 2026-10-31, mobile): where a released drag lands. dx = finger travel (px, + = right), v = speed (px/ms),
  // w = image width. Past 18 % of the width or a flick (> 0.45 px/ms over 24 px) goes to the next (left) or previous (right) photo;
  // the ends do not wrap (the drag rubber-bands there). Returns the new index or `current` to snap back.
  function swipeTarget(dx, v, w, current, count) {
    var far = Math.abs(dx) > Math.max(40, w * 0.18) || (Math.abs(v) > 0.45 && Math.abs(dx) > 24);
    if (!far) return current;
    var next = current + (dx < 0 ? 1 : -1);
    return next < 0 || next >= count ? current : next;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { getNextThumbIndex: getNextThumbIndex, swipeTarget: swipeTarget };

  if (typeof customElements === 'undefined' || customElements.get('nc-gallery')) return;

  class NcGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.wireSwipe();
      // a broken thumbnail keeps its slot and shows the alert icon instead of the browser's broken-image glyph
      var mark = function (img) { img.closest('.nc-gallery__thumb').setAttribute('data-media-error', ''); };
      this.querySelectorAll('.nc-gallery__thumb img').forEach(function (img) {
        if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) mark(img);
        else img.addEventListener('error', function () { mark(img); });
      });
    }

    get index() {
      var list = this.thumbs;
      var i = list.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
      return i < 0 ? 0 : i;
    }

    // touch and pen: the photo follows the finger sideways and lands on the next / previous one; vertical moves stay the page's scroll
    // (touch-action: pan-y). A drag that moved is not a tap: the click after it is swallowed.
    wireSwipe() {
      var self = this;
      var main = this.querySelector('.nc-gallery__main');
      if (!main || this.swipeWired) return;
      this.swipeWired = true;
      var g = null;
      var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      main.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' || self.thumbs.length < 2 || e.target.closest('button')) return;
        g = { x: e.clientX, y: e.clientY, t: e.timeStamp, id: e.pointerId, axis: '', dx: 0, lx: e.clientX, lt: e.timeStamp, v: 0 };
      });
      main.addEventListener('pointermove', function (e) {
        if (!g || e.pointerId !== g.id) return;
        var dx = e.clientX - g.x, dy = e.clientY - g.y;
        if (!g.axis) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (g.axis === 'y') { g = null; return; }
          try { main.setPointerCapture(e.pointerId); } catch (err) { /* old browsers */ }
          main.setAttribute('data-swiping', '');
        }
        var n = self.thumbs.length, i = self.index;
        var edge = (i === 0 && dx > 0) || (i === n - 1 && dx < 0);
        g.dx = edge ? dx / 3 : dx; // rubber band at the ends
        var dt = e.timeStamp - g.lt;
        if (dt > 0) g.v = (e.clientX - g.lx) / dt;
        g.lx = e.clientX; g.lt = e.timeStamp;
        var img = main.querySelector('img');
        if (img) img.style.transform = 'translateX(' + g.dx + 'px)';
      });
      function end(e) {
        if (!g || e.pointerId !== g.id) return;
        var gest = g; g = null;
        if (gest.axis !== 'x') return;
        main.removeAttribute('data-swiping');
        self.swallowClick = true;
        setTimeout(function () { self.swallowClick = false; }, 350);
        var img = main.querySelector('img');
        var w = main.clientWidth || 1;
        var from = self.index;
        var to = swipeTarget(gest.dx, gest.v, w, from, self.thumbs.length);
        if (!img) return;
        if (to === from || calm || !img.animate) {
          if (img.animate && !calm) img.animate([{ transform: 'translateX(' + gest.dx + 'px)' }, { transform: 'translateX(0)' }], { duration: 220, easing: 'cubic-bezier(.2,.8,.2,1)' });
          img.style.transform = '';
          if (to !== from) self.select(to, false, 'swipe');
          return;
        }
        var dir = to > from ? -1 : 1;
        img.animate([{ transform: 'translateX(' + gest.dx + 'px)' }, { transform: 'translateX(' + dir * w + 'px)', opacity: 0.6 }], { duration: 180, easing: 'cubic-bezier(.4,0,1,1)' }).onfinish = function () {
          self.select(to, false, 'swipe');
          img.style.transform = '';
          img.animate([{ transform: 'translateX(' + -dir * w * 0.35 + 'px)', opacity: 0.4 }, { transform: 'translateX(0)', opacity: 1 }], { duration: 260, easing: 'cubic-bezier(.2,.8,.2,1)' });
        };
      }
      main.addEventListener('pointerup', end);
      main.addEventListener('pointercancel', end);
      main.addEventListener('click', function (e) { if (self.swallowClick) { e.preventDefault(); e.stopPropagation(); } }, true);
    }

    get thumbs() {
      return Array.prototype.slice.call(this.querySelectorAll('.nc-gallery__thumb'));
    }

    onClick(event) {
      var thumb = event.target.closest('.nc-gallery__thumb');
      if (thumb) this.select(this.thumbs.indexOf(thumb), false);
    }

    onKeydown(event) {
      var list = this.thumbs;
      var i = list.indexOf(event.target);
      if (i < 0) return;
      var next = getNextThumbIndex(event.key, i, list.length);
      if (next === null) return;
      event.preventDefault();
      this.select(next, true);
    }

    select(i, focus, via) {
      var list = this.thumbs;
      var thumb = list[i];
      if (!thumb) return;
      var changed = thumb.getAttribute('aria-current') !== 'true';
      list.forEach(function (t) {
        t.removeAttribute('aria-current');
        t.setAttribute('tabindex', '-1');
      });
      thumb.setAttribute('aria-current', 'true');
      thumb.setAttribute('tabindex', '0');
      if (focus) thumb.focus();

      var main = this.querySelector('.nc-gallery__main');
      var img = main && main.querySelector('img');
      if (img && img.getAttribute('src') !== thumb.getAttribute('data-src')) {
        main.removeAttribute('data-media-error');
        img.setAttribute('src', thumb.getAttribute('data-src'));
        img.setAttribute('alt', thumb.getAttribute('data-alt') || '');
      }
      if (!changed) return;
      if (via === 'swipe') {
        // the strip follows the photo (its active thumbnail stays in view, without scrolling the page) and a light tick where supported
        var strip = this.querySelector('.nc-gallery__thumbs');
        if (strip && strip.scrollWidth > strip.clientWidth) strip.scrollTo({ left: Math.max(0, thumb.parentNode.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2), behavior: 'smooth' });
        if (navigator.vibrate) { try { navigator.vibrate(6); } catch (e) { /* not allowed */ } }
      }
      var status = this.querySelector('[data-nc-gallery-status]');
      if (status) status.textContent = thumb.getAttribute('aria-label');
      this.dispatchEvent(new CustomEvent('nc-gallery:change', {
        bubbles: true,
        detail: { id: this.id, index: i + 1, via: via || 'thumb' }
      }));
    }
  }

  customElements.define('nc-gallery', NcGallery);
})();
